import React, { useState } from "react";
import { Sparkles, RefreshCw, Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export function AIAssistButton({ endpoint, draft, onAccept, multiline = false, label = "Create with AI", testid = "ai" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [editing, setEditing] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { draft: draft || "" });
      setSuggestion(data.suggestion || "");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is unavailable right now. Please try again.");
      if (!suggestion) setOpen(false);
    } finally { setBusy(false); }
  };

  const start = async () => { setOpen(true); setSuggestion(""); await generate(); };
  const accept = () => { onAccept(suggestion); setOpen(false); toast.success("Applied — you can still edit it"); };

  return (
    <>
      <button type="button" data-testid={`${testid}-btn`} onClick={start}
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-gradient-to-r from-[#541224] to-[#751A32] border border-[#D4AF37]/50 text-[#F3D98B] hover:border-[#D4AF37] transition-colors">
        <Sparkles size={13} /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70" data-testid={`${testid}-modal`}>
          <div className="w-full max-w-lg rounded-2xl border border-[#D4AF37]/30 bg-[#141113] p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-lg text-[#FBF8F3] flex items-center gap-2"><Sparkles size={16} className="text-[#D4AF37]" /> AI Suggestion</h3>
              <button onClick={() => setOpen(false)} className="text-[#9E978E] hover:text-[#FBF8F3]"><X size={18} /></button>
            </div>
            <p className="text-[11px] text-[#9E978E] mb-3">Generated only from details you provided — review before applying. Nothing is saved until you accept.</p>
            {busy ? (
              <div className="py-10 text-center text-[#C9A96E] text-sm flex items-center justify-center gap-2"><RefreshCw size={15} className="animate-spin" /> Crafting something polished…</div>
            ) : editing ? (
              <textarea data-testid={`${testid}-edit`} rows={multiline ? 5 : 2} value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                className="w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/30 outline-none px-3 py-2.5 text-sm text-[#FBF8F3] resize-none" />
            ) : (
              <div data-testid={`${testid}-suggestion`} className="rounded-xl bg-[#0C0A0A]/50 border border-white/10 px-4 py-3 text-[#FBF8F3] text-sm leading-relaxed min-h-[60px]">
                {suggestion || "No suggestion"}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button data-testid={`${testid}-accept`} onClick={accept} disabled={busy || !suggestion}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-[#2E7D52] text-white hover:bg-[#276844] disabled:opacity-40"><Check size={14} /> Accept</button>
              <button data-testid={`${testid}-regenerate`} onClick={generate} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] disabled:opacity-40"><RefreshCw size={14} /> Regenerate</button>
              <button data-testid={`${testid}-toggle-edit`} onClick={() => setEditing((v) => !v)} disabled={busy || !suggestion}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-white/15 text-[#D8D2C9] hover:text-[#FBF8F3] disabled:opacity-40"><Pencil size={14} /> {editing ? "Done" : "Edit"}</button>
              <button data-testid={`${testid}-cancel`} onClick={() => setOpen(false)}
                className="ml-auto text-sm text-[#9E978E] hover:text-[#FBF8F3]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
